import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import type { TableColumnType, TableRow } from '_atoms/Table/types';
import { Context } from '_templates/AnalyticsDashboard/analytics-dashboard';
import { TableHeader } from '_molecules';
import { ReactComponent as EmptyImage } from 'dist/images/common/icons/changelog.svg';
import { ReactComponent as CircularProgress } from 'dist/icons/loading.svg';
import { analyticWidgets } from 'actions/analyticActions';
import { columnsMapper, dataMapper, type SscStatisticsResponse, type SscTableRow } from './utils';
import { ROW_NAMES } from './constants';
import './style.scss';

interface EmptyTableProps {
  loading?: boolean;
}

const EmptyTable = ({ loading }: EmptyTableProps) => {
  const { t } = useTranslation();
  return (
    <div className="empty-table">
      <EmptyImage className="empty-table__image" />
      <Typography.Title2 className="empty-table__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:analytics_ssc_info.text_no_data_found')}
      </Typography.Title2>
      <Typography.Title3 className="empty-table__subtitle" weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:analytics_ssc_info.text_try_another_criteria')}
      </Typography.Title3>
      {loading && (
        <div className="empty-table__loading">
          <CircularProgress width={40} height={40} />
        </div>
      )}
    </div>
  );
};

export const AnalyticsSSCInfo = () => {
  const { t } = useTranslation();
  const { appliedFilters } = useContext(Context)!;

  const [data, setData] = useState<SscTableRow[]>([]);
  const [columns, setColumns] = useState<TableColumnType[]>([]);
  const [loading, setLoading] = useState(false);

  const onApplyAnalytics = () => {
    setLoading(true);
    analyticWidgets('self-submitted-candidates-statistics', appliedFilters, null)
      .then(resp => {
        const response = resp as SscStatisticsResponse;
        if (ROW_NAMES.reduce((total, { totalKey }) => total + response.analytics_total[totalKey], 0)) {
          setColumns(columnsMapper(response));
          setData(dataMapper(response));
        } else {
          setColumns([]);
          setData([]);
        }
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(onApplyAnalytics, [appliedFilters]);

  return (
    <Card className="ssc-statistics">
      <TableHeader title={t('translation:analytics_ssc_info.card_title')} />
      {columns.length ? (
        <Table
          loading={loading}
          rowKey="id"
          columns={columns}
          data={data as TableRow[]}
          showCardHeader={false}
          showCardFooter={false}
        />
      ) : (
        <EmptyTable loading={loading} />
      )}
    </Card>
  );
};
